/**
 * PWA — «ứng dụng cài được» trên điện thoại (03/10/2026). Phần tính toán thuần: máy này cài bằng cách
 * nào, khi nào nên hỏi máy chủ xem có bản mới. Việc đăng ký service worker và bắt sự kiện của trình
 * duyệt nằm ở `stores/pwa-store.ts`.
 */

/**
 * Cách cài trên máy đang mở:
 *  - `installed` đang chạy như một ứng dụng (mở từ màn hình chính) — không mời cài nữa;
 *  - `prompt`    trình duyệt đã đưa sẵn hộp cài (Chrome / Edge / Cốc Cốc trên Android và máy tính);
 *  - `ios`       iPhone / iPad: Apple không cho trang web mở hộp cài, phải chỉ đường qua nút Chia sẻ;
 *  - `none`      không cài được ở đây (trình duyệt không hỗ trợ, hoặc trình duyệt nhúng trong Zalo…).
 */
export type InstallMode = 'installed' | 'prompt' | 'ios' | 'none';

export interface InstallEnvironment {
  standalone: boolean;
  hasPrompt: boolean;
  userAgent: string;
  /** iPadOS tự xưng là máy Mac — chỉ phân biệt được bằng số điểm chạm. */
  maxTouchPoints: number;
}

export function isIosDevice(userAgent: string, maxTouchPoints: number): boolean {
  return (
    /iphone|ipad|ipod/i.test(userAgent) || (/macintosh/i.test(userAgent) && maxTouchPoints > 1)
  );
}

/**
 * Trình duyệt NHÚNG trong ứng dụng khác (Zalo, Facebook, Messenger, Instagram, Line, TikTok): mở link từ
 * tin nhắn là rơi vào đây, và chúng không cho «Thêm vào Màn hình chính».
 */
export function isInAppBrowser(userAgent: string): boolean {
  return /zalo|fban|fbav|fb_iab|instagram|line\/|tiktok|musical_ly/i.test(userAgent);
}

export function installMode(environment: InstallEnvironment): InstallMode {
  if (environment.standalone) return 'installed';
  if (environment.hasPrompt) return 'prompt';
  if (isInAppBrowser(environment.userAgent)) return 'none';
  return isIosDevice(environment.userAgent, environment.maxTouchPoints) ? 'ios' : 'none';
}

/**
 * Mục PWA trong menu tài khoản: máy cài được thì «Cài ứng dụng»; đang chạy như ứng dụng (không có nút tải
 * lại của trình duyệt) thì «Tải lại ứng dụng»; còn lại không thêm gì.
 */
export type AccountMenuAction = 'install' | 'reload' | null;

export function accountMenuAction(mode: InstallMode): AccountMenuAction {
  if (mode === 'prompt' || mode === 'ios') return 'install';
  return mode === 'installed' ? 'reload' : null;
}

/** Hỏi máy chủ xem có bản mới không quá dày thì phí pin, quá thưa thì máy treo cả ngày chạy bản cũ. */
export const UPDATE_CHECK_INTERVAL_MS = 30 * 60_000;

export function shouldCheckForUpdate(lastCheckedAt: number, now: number): boolean {
  return now - lastCheckedAt >= UPDATE_CHECK_INTERVAL_MS;
}

/**
 * Tải mã của một màn hình thất bại (bản cũ đã bị thay trên máy chủ): tự tải lại trang MỘT lần. Lần tải
 * lại vừa xong mà vẫn lỗi thì thôi — không thì mất mạng là trang tự tải lại mãi không dừng.
 */
export const RELOAD_GUARD_MS = 60_000;

export function shouldReloadAfterChunkError(lastReloadAt: number, now: number): boolean {
  return now - lastReloadAt > RELOAD_GUARD_MS;
}
